import React, { useEffect, useState, useRef, useContext, useMemo, useCallback } from "react";
import axios from "axios";

import io from "socket.io-client";
import { TagForumViewNode } from "../../components/ForumMode/TagBasedForum/TagForumViewNode";
import { QuestionnaireNodeCreator } from "../../components/ForumMode/core/QuestionnaireNodeCreator";
import { NodeTypeSelector } from "../../components/ForumMode/core/NodeTypeSelector";
import { resolveViewNodeComponent } from "../../components/ForumMode/nodeViewRegistry";
import Notice from "../../components/Notice";
import url from "../../url.js";
import { genEdge, genNode_without_emoji, newNode, newEdge } from "../../utils/ideaTool";
import { ModeContext } from "../../contexts/ModeContext";
import { parseContentToHtml } from '../../utils/markdownParser';
import { TagForumTaggedNodeCreator } from "../../components/ForumMode/TagBasedForum/TagForumTaggedNodeCreator";
import './TagBasedForum.css';
import '../../styles/NodeContentStyles.css'; // 導入節點內容樣式
import { getInitialConfig, normalizeNodeTag } from '../../utils/activityInfoAccessors';
import { buildTaggedForumContext } from "../../components/ForumMode/TagBasedForum/taggedForumContext";
import useUnreadMessages from "../../hooks/useUnreadMessages";
import GroupToolPanel from "../../components/GroupToolPanel";
import { getNodeType } from "../../utils/nodeTypeUtils";

// 標籤配置
const TAG_CONFIG = {
  idea: { 
    label: '想法', 
    emoji: '💡', 
    color: '#F3E8FF', 
    description: '分享你的創意想法和靈感'
  }
};

/**
 * 验证并修正节点标签
 * 规则：只有负ID的节点才能使用 'initial' 标签
 * @param {string} tag - 原始标签
 * @param {number} nodeId - 节点ID
 * @param {string} context - 调用上下文（用于日志）
 * @returns {string} 修正后的标签
 */
const validateAndFixTag = (tag, nodeId, context = '') => {
  const normalizedTag = normalizeNodeTag(tag, nodeId);
  
  // 只有负ID的节点才能使用 'initial' 标签
  if ((tag === 'initial' || (Array.isArray(tag) && tag.includes('initial'))) &&
      normalizedTag === 'idea' && typeof nodeId === 'number' && nodeId > 0) {
    console.warn(`[TagBasedForum${context}] 节点 ${nodeId} 不应使用 initial 标签，自动转换为 idea`);
  }
  
  return normalizedTag;
};

export default function TagBasedForum({ showNavbar = true }) {
  // ✅ 初始化：將 localStorage 同步到 sessionStorage
  useEffect(() => {
    const syncStorage = () => {
      const activityId = localStorage.getItem('activityId');
      const groupId = localStorage.getItem('groupId');
      const userId = localStorage.getItem('userId');
      const joinCode = localStorage.getItem('joinCode');
      
      if (activityId) sessionStorage.setItem('activityId', activityId);
      if (groupId) sessionStorage.setItem('groupId', groupId);
      if (userId) sessionStorage.setItem('userId', userId);
      if (joinCode) sessionStorage.setItem('joinCode', joinCode);
      
      console.log('[TagBasedForum] Storage 同步完成:', { activityId, groupId, userId, joinCode });
    };
    
    syncStorage();
  }, []);
  
  const isGuestMode = sessionStorage.getItem('isGuestMode') === 'true';
  const activityInfo = JSON.parse(localStorage.getItem('activityInfo'));
  const { isMapBoard } = useContext(ModeContext);
  const { updateLastReadTime, clearCache } = useUnreadMessages();
  const [open, setOpen] = useState(false);
  const [nodeContent, setNodeContent] = useState(null);
  const [ws, setWS] = useState(null);
  const [pendingAINodes, setPendingAINodes] = useState([]);
  const [notices, setNotices] = useState([]);
  const activityId = sessionStorage.getItem("activityId");
  const [groupId, setGroupId] = useState(sessionStorage.getItem('groupId'));
  const [createOpen, setCreateOpen] = useState(false);
  const [nodeTypeSelectorOpen, setNodeTypeSelectorOpen] = useState(false);
  const [questionnaireCreateOpen, setQuestionnaireCreateOpen] = useState(false);
  
  // 節點列表容器引用，用於自動滾動
  const nodesListRef = useRef(null);
  
  // 節點數據按標籤分類
  const [nodesByTag, setNodesByTag] = useState({});
  const [activeTag, setActiveTag] = useState('initial'); // 默認顯示引導單元
  const [initialNodes, setInitialNodes] = useState([]); // 引導單元（活動說明和初始卡片）
  const [allNodes, setAllNodes] = useState([]); // 存儲所有節點
  const [allEdges, setAllEdges] = useState([]); // 存儲所有邊數據
  
  // 篩選功能
  const [searchTerm, setSearchTerm] = useState(''); // 搜索關鍵字
  const [sortBy, setSortBy] = useState('oldest'); // 排序方式: oldest, newest, author - 默認新訊息在下方
  const [filterAuthor, setFilterAuthor] = useState('all'); // 作者篩選
  
  // 標記已讀：組件掛載時更新 lastRead，並定期更新（每 2 分鐘）
  useEffect(() => {
    const gid = sessionStorage.getItem('groupId');
    const aid = sessionStorage.getItem('activityId');
    if (!gid || !aid || !updateLastReadTime) return;

    const groupIdNum = parseInt(gid, 10);
    const activityIdNum = parseInt(aid, 10);

    // 立即更新一次
    updateLastReadTime(groupIdNum, activityIdNum).catch(err => 
      console.error('TagBasedForum 標記已讀失敗:', err)
    );

    // 每 2 分鐘定期更新
    const interval = setInterval(() => {
      updateLastReadTime(groupIdNum, activityIdNum).catch(err => 
        console.error('TagBasedForum 定期更新已讀失敗:', err)
      );
    }, 120000); // 2 分鐘

    return () => clearInterval(interval);
  }, [groupId, updateLastReadTime]);

  // 頁面可見性變化時更新（切換回標籤頁時）
  useEffect(() => {
    const gid = sessionStorage.getItem('groupId');
    const aid = sessionStorage.getItem('activityId');
    if (!gid || !aid || !updateLastReadTime) return;

    const groupIdNum = parseInt(gid, 10);
    const activityIdNum = parseInt(aid, 10);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        updateLastReadTime(groupIdNum, activityIdNum).catch(err => 
          console.error('TagBasedForum 可見性變化更新已讀失敗:', err)
        );
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [groupId, updateLastReadTime]);
  
  // 從節點數據中動態提取標籤配置
  const allTagConfigs = useMemo(() => {
    const dynamicTags = { ...TAG_CONFIG };
    
    // 從所有節點中提取標籤
    allNodes.forEach(node => {
      const tag = normalizeNodeTag(node.tags ?? node.rawData?.tags, node.id);
      // 排除 'initial' 標籤，因為它已經有專門的「活動說明」標籤頁
      if (tag && tag !== 'initial' && !dynamicTags[tag]) {
        // 為未知標籤創建默認配置
        dynamicTags[tag] = {
          label: tag.charAt(0).toUpperCase() + tag.slice(1),
          emoji: '🏷️',
          color: '#E5E7EB',
          description: `自定義標籤：${tag}`
        };
      }
    });
    
    return dynamicTags;
  }, [allNodes]);

  // Helper 函數：安全獲取標籤顯示文本
  const getTagDisplayText = useCallback((tagKey) => {
    const tag = allTagConfigs[tagKey];
    if (!tag) return '未知標籤';
    return `${tag.emoji || ''} ${tag.label || tagKey}`.trim();
  }, [allTagConfigs]);

  // Helper 函數：安全獲取標籤顏色
  const getTagColor = useCallback((tagKey) => {
    const tag = allTagConfigs[tagKey];
    return tag?.color || '#E5E7EB';
  }, [allTagConfigs]);

  // Helper 函數：安全獲取標籤描述
  const getTagDescription = useCallback((tagKey) => {
    const tag = allTagConfigs[tagKey];
    return tag?.description || `標籤：${tagKey}`;
  }, [allTagConfigs]);

  // 建立節點階層結構
  const buildHierarchy = useCallback((nodes, edges) => {
    // 調試模式下顯示邊的信息
    if (process.env.NODE_ENV === 'development' && edges.length > 0) {
      console.log('buildHierarchy 輸入:', {
        nodeCount: nodes.length,
        edgeCount: edges.length,
        sampleEdge: edges[0]
      });
    }
    
    if (!nodes || nodes.length === 0) {
      return [];
    }
    
    // 建立父子關係映射
    const childrenMap = new Map();
    const parentMap = new Map();
    
    // 初始化每個節點的子節點列表
    nodes.forEach(node => {
      const nodeId = String(node.id);
      childrenMap.set(nodeId, []);
    });
    
    // 根據邊建立父子關係
    edges.forEach(edge => {
      const parentId = String(edge.to);
      const childId = String(edge.from);
      
      // 只處理存在於當前節點列表中的關係
      if (childrenMap.has(parentId) && nodes.some(n => String(n.id) === childId)) {
        childrenMap.get(parentId).push(childId);
        parentMap.set(childId, parentId);
      }
    });
    
    // 找出根節點（沒有父節點的節點）
    const rootNodes = nodes.filter(node => !parentMap.has(String(node.id)));
    
    // 遞歸函數來建立階層結構
    const buildHierarchyRecursive = (nodeId, depth = 0, visited = new Set()) => {
      if (visited.has(nodeId)) {
        return []; // 避免循環引用
      }
      visited.add(nodeId);
      
      const node = nodes.find(n => String(n.id) === nodeId);
      if (!node) {
        return [];
      }
      
      const result = [{ ...node, depth }];
      
      // 添加子節點
      const children = childrenMap.get(nodeId) || [];
      children
        .sort((a, b) => {
          const nodeA = nodes.find(n => String(n.id) === a);
          const nodeB = nodes.find(n => String(n.id) === b);
          if (!nodeA || !nodeB) return 0;
          return new Date(nodeA.createdAt) - new Date(nodeB.createdAt); // 子節點按時間排序
        })
        .forEach(childId => {
          result.push(...buildHierarchyRecursive(childId, depth + 1, new Set(visited)));
        });
      
      return result;
    };
    
    // 構建最終的階層結構
    let hierarchicalNodes = [];
    
    // 首先處理根節點，按時間排序（舊的在上，新的在下）
    rootNodes
      .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
      .forEach(rootNode => {
        hierarchicalNodes.push(...buildHierarchyRecursive(String(rootNode.id)));
      });
    
    return hierarchicalNodes;
  }, []);
  
  // 確保 activeTag 在可用標籤列表中
  useEffect(() => {
    const availableTags = ['initial', ...Object.keys(allTagConfigs)];
    if (availableTags.length > 0 && !availableTags.includes(activeTag)) {
      setActiveTag(availableTags[0]);
    }
  }, [allTagConfigs, activeTag]);

  // 當節點或邊數據變化時，重新構建階層結構
  useEffect(() => {
    if (allNodes.length > 0) {
      // 按標籤分類節點，並建立階層結構
      const groupedNodes = allNodes.reduce((acc, node) => {
        const tag = validateAndFixTag(node.tags, node.id, ' grouping');
        
        if (!acc[tag]) acc[tag] = [];
        acc[tag].push(node);
        return acc;
      }, {});

      // 為每個分類建立階層結構
      const newNodesByTag = {};
      Object.keys(groupedNodes).forEach(tag => {
        try {
          newNodesByTag[tag] = buildHierarchy(groupedNodes[tag], allEdges);
        } catch (error) {
          console.error(`構建標籤 ${tag} 階層結構失敗:`, error);
          // 如果構建失敗，使用原始節點順序
          newNodesByTag[tag] = groupedNodes[tag].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        }
      });

      setNodesByTag(newNodesByTag);
    }
  }, [allNodes, allEdges, buildHierarchy]);
  
  // WebSocket 和數據獲取邏輯 (從 ForumSticker.js 複製)
  const fetchGroupData = async () => {
    const jwtToken = localStorage.getItem('jwtToken');
    const headers = jwtToken ? { Authorization: `Bearer ${jwtToken}` } : undefined;
    const isGuestMode = sessionStorage.getItem('isGuestMode') === 'true';
    
    // ✅ 安全獲取參數
    const joinCode = sessionStorage.getItem('joinCode');
    const activityId = sessionStorage.getItem("activityId");
    const userId = sessionStorage.getItem("userId");
    
    // ✅ 參數驗證（訪客模式跳過 userId 檢查）
    if (!isGuestMode && (!activityId || activityId === 'null' || !userId || userId === 'null')) {
      console.error('[TagBasedForum] 缺少必要參數:', { activityId, userId });
      alert('無法獲取活動資訊，請重新進入活動');
      window.location.href = '/main';
      return null;
    }
    
    // 訪客模式不需要獲取群組資料，直接返回
    if (isGuestMode) {
      console.log('[TagBasedForum] 訪客模式，跳過獲取群組資料');
      return await getNodes();
    }
    
    const enterDifferentGroupEndpoint = `${url.backendHost}api/groups/teacher/${joinCode}/${userId}`;
    const getMyGroupEndpoint = `${url.backendHost}api/groups/${activityId}/${userId}`;
    
    console.log('[TagBasedForum] 獲取群組資料:', { activityId, userId, joinCode });

    try {
      if (!joinCode) throw new Error('no-join-code');
      const response = await axios.get(enterDifferentGroupEndpoint, { headers, withCredentials: true });
      const fetchedGroupId = response.data.data[0].id;
      localStorage.setItem("groupId", fetchedGroupId);
      sessionStorage.setItem("groupId", fetchedGroupId);
      setGroupId(fetchedGroupId);
    } catch (error) {
      try {
        const response = await axios.get(getMyGroupEndpoint, { headers, withCredentials: true });
        const fetchedGroupId = response.data.data[0].id;
        localStorage.setItem("groupId", fetchedGroupId);
        sessionStorage.setItem("groupId", fetchedGroupId);
        setGroupId(fetchedGroupId);
      } catch (error) {
        console.error("獲取群組資料時出錯", error);
      }
    } finally {
      return await getNodes();
    }
  };

  const getNodes = async () => {
    // ✅ 安全獲取 groupId
    const groupId = sessionStorage.getItem("groupId");
    const isGuestMode = sessionStorage.getItem('isGuestMode') === 'true';
    const activityId = sessionStorage.getItem('activityId');
    
    if (!groupId || groupId === 'null') {
      console.log('[TagBasedForum] groupId 無效，嘗試獲取群組資料');
      if (!isGuestMode) {
        await fetchGroupData();
      }
      return;
    }

    try {
      const jwtToken = localStorage.getItem('jwtToken');
      
      // 訪客模式使用公開 API，否則使用認證 API
      let fetchData, fetchEdge;
      if (isGuestMode) {
        // 訪客模式：不發送 JWT token，使用公開 API
        fetchData = await axios.get(`${url.backendHost}api/guest/${activityId}/${groupId}/nodes`);
        fetchEdge = await axios.get(`${url.backendHost}api/guest/${activityId}/${groupId}/edges`);
      } else {
        // 認證模式：發送 JWT token
        const headers = jwtToken ? { Authorization: `Bearer ${jwtToken}` } : undefined;
        fetchData = await axios.get(`${url.backendHost}api/nodes/all/${groupId}`, { headers, withCredentials: true });
        fetchEdge = await axios.get(`${url.backendHost}api/edges/all/${groupId}`, { headers, withCredentials: true });
      }
      
      // 處理節點數據：訪客模式和認證模式的數據結構不同
      const nodeData = (isGuestMode ? fetchData.data : fetchData.data[0].Nodes).map((node) => {
        const createdAt = node.createdAt ? new Date(node.createdAt) : null;
        const formattedCreatedAt = createdAt ? createdAt.toISOString() : 'Invalid Date';

        return {
          ...genNode_without_emoji(node),
          createdAt: formattedCreatedAt,
          author: node.author,
          tags: validateAndFixTag(node.tags, node.id, ' getNodes'),
          rawData: node // 保存原始數據
        };
      });

      const edgeData = fetchEdge.data.map((edge) => ({
        from: edge.from,
        to: edge.to,
        groupId: edge.groupId
      }));

      if (!Array.isArray(nodeData)) { 
        addNotice("Invalid node data format", "error");
        throw new Error("Invalid node data format");
      }

      // Add initial nodes if they exist
      let allNodeData = [...nodeData];
      try {
        const { loadInitialNodesForTaggedForum, validateInitialNodes } = require('../../utils/initialNodesLoader');
        const groupId = sessionStorage.getItem('groupId');
        const initialNodes = loadInitialNodesForTaggedForum(activityInfo, groupId, true);
        
        if (initialNodes.length > 0) {
          // 验证节点有效性
          if (!validateInitialNodes(initialNodes)) {
            console.error('[TagBasedForum] Invalid initial nodes detected');
          }
          allNodeData = [...initialNodes, ...allNodeData];
        }
      } catch (e) {
        console.error('[TagBasedForum] Error loading initial nodes in getNodes:', e);
      }

      // 設置節點和邊數據（階層結構會由 useEffect 自動構建）
      setAllNodes(allNodeData);
      setAllEdges(edgeData);
      addNotice("Data loaded successfully", "success");
      
      return { nodes: allNodeData, edges: edgeData };
    } catch (error) {
      console.error('Error fetching data:', error);
      addNotice("Failed to load data. Please try again.", "error");
      if (error.response?.status === 401) {
        addNotice("Session expired. Please login again.", "warning");
        window.location.href = "/";
      }
      throw error;
    }
  };

  // 處理節點點擊
  const handleNodeClick = (node) => {
    setNodeContent(null);
    setOpen(true);
    
    // 初始節點（包含問卷初始節點）帶有 isInitial 標記，直接用本地數據，不呼叫後端 API
    const isInitialNode = node?.isInitial === true;
    if (isInitialNode) {
      setNodeContent({ 
        ...node,
        content: node.title,
        title: node.label 
      });
    } else {
      fetchNode(node.id || node.rawData?.id);
    }
  };

  const fetchNode = async (nodeId) => {
    const isGuestMode = sessionStorage.getItem('isGuestMode') === 'true';
    const activityId = sessionStorage.getItem('activityId');
    const groupId = sessionStorage.getItem('groupId');
    
    try {
      let response;
      if (isGuestMode) {
        // 访客模式：使用公开 API
        response = await axios.get(`${url.backendHost}api/guest/${activityId}/${groupId}/node/${nodeId}`);
      } else {
        // 认证模式：使用认证 API
        response = await axios.get(`${url.backendHost}api/nodes/${nodeId}`);
      }
      setNodeContent({ 
        ...response.data, 
        renderedContent: { __html: response.data.content }
      });
    } catch (err) {
      console.error(err);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  // 初始化
  useEffect(() => {
    let isActive = true;
    (async () => {
      setWS(io.connect(url.socketioHost, {path: '/s/socket.io'}));
      // Load initial nodes from activityInfo
      try {
        const { loadInitialNodesForTaggedForum, validateInitialNodes } = require('../../utils/initialNodesLoader');
        const groupId = sessionStorage.getItem('groupId');
        const initialNodes = loadInitialNodesForTaggedForum(activityInfo, groupId, true);
        
        if (initialNodes.length > 0) {
          // 验证节点有效性
          if (!validateInitialNodes(initialNodes)) {
            console.error('[TagBasedForum] Invalid initial nodes detected in initialization');
          }
          setInitialNodes(initialNodes);
        }
      } catch (e) {
        console.error('[TagBasedForum] Failed to load initial nodes:', e);
      }
      await fetchGroupData();
    })();
    return () => { isActive = false; };
  }, []);

  // WebSocket 事件監聽
  useEffect(() => {
    if (ws) {
      ws.on("connect", () => {
        addNotice("Connected to server successfully", "success");
      });

      ws.on(`node-recieve-${activityId}`, (body) => {
        if (body.groupId == sessionStorage.getItem('groupId')) {
          const newNodeData = {
            createdAt: body.createdAt || new Date().toISOString(),
            author: body.author,
            tags: validateAndFixTag(body.tags, body.id, ' WebSocket'),
            rawData: body,
            ...genNode_without_emoji(body),
          };
          
          // 更新所有節點
          setAllNodes(prev => [...prev, newNodeData]);
          
          // 收到新消息，清除該群組的未讀快取（其他群組會顯示紅點）
          if (clearCache) {
            clearCache(body.groupId);
          }
        }
      });

      ws.on(`edge-recieve-${activityId}`, (body) => {
        if (body.groupId == sessionStorage.getItem("groupId")) {
          const newEdge = {
            from: body.from,
            to: body.to,
            groupId: body.groupId
          };
          
          // 更新邊數據
          setAllEdges(prev => [...prev, newEdge]);
        }
      });

      return () => {
        ws.off(`node-recieve-${activityId}`);
        ws.off(`edge-recieve-${activityId}`);
        ws.off("connect");
      };
    }
  }, [ws, activityId, groupId]);

  // 通知函數
  const addNotice = (message, type = 'info') => {
    const id = Date.now();
    setNotices(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotices(prev => prev.filter(notice => notice.id !== id));
    }, 5000);
  };

  const removeNotice = (id) => {
    setNotices(prev => prev.filter(notice => notice.id !== id));
  };

  // 獲取所有作者列表
  const getAuthors = useMemo(() => {
    const authors = new Set();
    allNodes.forEach(node => {
      if (node.author) authors.add(node.author);
    });
    return ['all', ...Array.from(authors)];
  }, [allNodes]);

  // 篩選節點（保持階層結構）
  const getFilteredNodes = useCallback((tag) => {
    let nodes = nodesByTag[tag] || [];
    
    // 搜索篩選
    if (searchTerm) {
      nodes = nodes.filter(node => {
        const title = (node.label?.split('\n')[0] || node.title || '').toLowerCase();
        const content = (node.title || '').toLowerCase();
        const author = (node.author || '').toLowerCase();
        const term = searchTerm.toLowerCase();
        return title.includes(term) || content.includes(term) || author.includes(term);
      });
    }
    
    // 作者篩選
    if (filterAuthor !== 'all') {
      nodes = nodes.filter(node => node.author === filterAuthor);
    }
    
    // 排序：只有在非預設情況下才重新排序，以保持階層結構
    if (sortBy !== 'oldest') {
      nodes = [...nodes].sort((a, b) => {
        switch (sortBy) {
          case 'newest':
            // 對於 newest 排序，我們需要特殊處理以保持階層關係
            // 首先按根節點分組，然後在每組內排序
            if (a.depth === 0 && b.depth === 0) {
              return new Date(b.createdAt) - new Date(a.createdAt);
            }
            // 如果不是同層級的比較，保持原有順序
            return 0;
          case 'author':
            return (a.author || '').localeCompare(b.author || '');
          default:
            return 0;
        }
      });
    }
    // sortBy === 'oldest' 時保持原有的階層結構順序
    
    return nodes;
  }, [nodesByTag, searchTerm, filterAuthor, sortBy]);

  // 獲取標籤統計（基於篩選後的結果）
  const getTagStats = () => {
    const stats = {};
    stats.initial = initialNodes.length;
    Object.keys(allTagConfigs).forEach(tag => {
      stats[tag] = getFilteredNodes(tag).length;
    });
    return stats;
  };

  const tagStats = getTagStats();
  // 根據 activeTag 選擇當前顯示的節點
  const currentNodes = activeTag === 'initial' 
    ? initialNodes // 直接使用已转换的初始节点，它们已经有正确的负整数 ID
    : getFilteredNodes(activeTag);

  // 自動滾動到底部函數
  const scrollToBottom = useCallback(() => {
    if (nodesListRef.current) {
      nodesListRef.current.scrollTop = nodesListRef.current.scrollHeight;
    }
  }, []);

  // 當節點列表變化時，自動滾動到底部顯示最新訊息
  // 注意：在階層結構模式下，暫時禁用自動滾動，讓用戶能看到完整的討論串
  useEffect(() => {
    // 只有在沒有階層結構時才自動滾動（例如當前沒有邊的情況）
    const hasHierarchy = currentNodes.some(node => node.depth > 0);
    if (sortBy === 'oldest' && currentNodes.length > 0 && !hasHierarchy) {
      setTimeout(() => scrollToBottom(), 100); // 小延遲確保DOM更新完成
    }
  }, [currentNodes.length, sortBy, scrollToBottom, activeTag, currentNodes]);

  return (
    <div className="tag-based-forum" style={showNavbar ? undefined : { position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', minHeight: 0, padding: 0 }}>
      
      {/* 通知容器 */}
      <div className="notice-container">
        {notices.map(notice => (
          <Notice
            key={notice.id}
            type={notice.type}
            message={notice.message}
            onClose={() => removeNotice(notice.id)}
          />
        ))}
      </div>

      {/* 主要內容區域 */}
      <div className="forum-content" style={showNavbar ? { 
        marginLeft: "max(5vw, 64px)",
        width: "calc(95vw - max(5vw, 64px))",
        height: "95vh",
        marginTop: "2vh"
      } : {
        width: "100%",
        height: "100vh"
      }}>
        
        {/* 簡化的標籤導航 */}
        <div className="tag-navigation-compact">
          <div className="tag-tabs-compact">
            {/* 活動說明/引導單元 Tab */}
            <button
              className={`tag-tab-compact ${activeTag === 'initial' ? 'active' : ''}`}
              onClick={() => setActiveTag('initial')}
              style={{ 
                borderColor: '#FFE4B5',
                backgroundColor: activeTag === 'initial' ? '#FFE4B5' : 'transparent'
              }}
            >
              <span className="tag-emoji">📋</span>
              <span className="tag-label">活動說明</span>
              <span className="tag-count">({tagStats.initial || 0})</span>
            </button>
            
            {Object.entries(allTagConfigs).map(([tag, config]) => (
              <button
                key={tag}
                className={`tag-tab-compact ${activeTag === tag ? 'active' : ''}`}
                onClick={() => setActiveTag(tag)}
                style={{ 
                  borderColor: config.color,
                  backgroundColor: activeTag === tag ? config.color : 'transparent'
                }}
              >
                <span className="tag-emoji">{config.emoji}</span>
                <span className="tag-label">{config.label}</span>
                <span className="tag-count">({tagStats[tag] || 0})</span>
              </button>
            ))}
            
            {!isGuestMode && (
              <button 
                className="create-btn-compact"
                onClick={() => setNodeTypeSelectorOpen(true)}
                title="新增想法"
              >
                <span className="create-btn-icon">＋</span>
              </button>
            )}
          </div>

          {/* 簡化的搜索工具 */}
          <div className="filter-toolbar-compact">
            <input
              type="text"
              placeholder="搜索..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input-compact"
            />
            
            <select 
              value={sortBy} 
              onChange={(e) => setSortBy(e.target.value)}
              className="filter-select-compact"
            >
              <option value="oldest">最舊</option>
              <option value="newest">最新</option>
              <option value="author">作者</option>
            </select>
            
            {searchTerm && (
              <button 
                className="clear-btn-compact"
                onClick={() => setSearchTerm('')}
                title="清除搜索"
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* 內容區域 */}
        <div className="tag-content-compact">

          {/* 節點列表 */}
          <div className="nodes-list" ref={nodesListRef}>
            {currentNodes.length > 0 ? (
              currentNodes.map((node, index) => (
                <div
                  key={node.id || index}
                  className={`node-card ${node.depth > 0 ? 'node-reply' : 'node-root'}`}
                  onClick={() => handleNodeClick(node)}
                  style={{ 
                    borderLeft: `4px solid ${getTagColor(activeTag)}`,
                    marginLeft: `${(node.depth || 0) * 20}px`,
                    marginBottom: node.depth > 0 ? '8px' : '16px'
                  }}
                >
                  {node.depth > 0 && (
                    <div className="reply-indicator">
                      <span className="reply-arrow">↳</span>
                      <span className="reply-label">回覆</span>
                    </div>
                  )}
                  
                  <div className="node-header">
                    <div className="user-avatar">
                      {node.author?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                    <div className="user-info">
                      <div className="node-title">
                        {node.label?.split('\n')[0] || node.title || '無標題'}
                      </div>
                      <div className="node-meta">
                        <span className="node-author">{node.author}</span>
                        <span>•</span>
                        <span className="node-time">
                          {new Date(node.createdAt).toLocaleString('zh-TW', {
                            month: 'numeric',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="node-content-preview">
                    {getNodeType(node) === 'questionnaire' ? (
                      (() => {
                        try {
                          const qData = JSON.parse(node.content || '{}');
                          const preview = qData.description || node.label || '問卷節點';
                          return <p>📋 問卷：{preview}</p>;
                        } catch (_) {
                          return <p>📋 問卷節點</p>;
                        }
                      })()
                    ) : node.title ? (
                      <div 
                        className="node-content-container"
                        dangerouslySetInnerHTML={{ 
                          __html: parseContentToHtml(node.title.substring(0, 150) + (node.title.length > 150 ? '...' : ''))
                        }} 
                      />
                    ) : (
                      <p>點擊查看詳細內容</p>
                    )}
                  </div>
                  
                  <div className="node-footer">
                    <div className="node-actions">
                      {!node.isInitial && <button 
                        className="action-btn reply-btn"
                        onClick={(e) => {
                          e.stopPropagation(); // 阻止事件冒泡，避免觸發節點點擊
                          handleNodeClick(node); // 打開節點詳情，其中包含回覆功能
                        }}
                      >
                        <span>💬</span>
                        <span>回覆</span>
                      </button>}
                    </div>
                    <span className="node-tag" style={{ backgroundColor: activeTag === 'initial' ? '#FFE4B5' : getTagColor(activeTag) }}>
                      {activeTag === 'initial' ? '📋 活動說明' : getTagDisplayText(activeTag)}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="empty-state">
                <div className="empty-icon">{activeTag === 'initial' ? '📋' : (allTagConfigs[activeTag]?.emoji || '📝')}</div>
                <h4>{activeTag === 'initial' ? '尚無活動說明' : `尚無 ${allTagConfigs[activeTag]?.label || '內容'}`}</h4>
                <p>{activeTag === 'initial' ? '活動主持人尚未設定活動說明或引導單元' : getTagDescription(activeTag)}</p>
                <button 
                  className="create-first-btn"
                  onClick={() => setNodeTypeSelectorOpen(true)}
                  disabled={activeTag === 'initial'}
                  style={{ opacity: activeTag === 'initial' ? 0.5 : 1, cursor: activeTag === 'initial' ? 'not-allowed' : 'pointer' }}
                >
                  {activeTag === 'initial' ? '請聯繫主持人設定活動說明' : `創建第一個 ${allTagConfigs[activeTag]?.label || '項目'}`}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 模態框 */}
      {(() => {
        const ViewNode = resolveViewNodeComponent(getNodeType(nodeContent), 'tagged-forum');
        if (!ViewNode) return null;
        return (
          <ViewNode
            open={open}
            onClose={handleClose}
            nodeContent={nodeContent}
            ws={ws}
            groupId={groupId}
            activityId={activityId}
            graphNodes={allNodes}
            graphEdges={allEdges}
            allTagConfigs={allTagConfigs}
            buildGroupContext={buildTaggedForumContext}
            mode="tagged-forum"
            onRefresh={getNodes}
            onNodeUpdated={(nodeId) => {
              if (nodeContent && nodeContent.id === nodeId) {
                fetchNode(nodeId);
              }
            }}
            onNodeDeleted={(nodeId) => {
              if (nodeContent && nodeContent.id === nodeId) {
                setNodeContent(null);
                setOpen(false);
              }
            }}
          />
        );
      })()}
      <NodeTypeSelector
        open={nodeTypeSelectorOpen}
        onClose={() => setNodeTypeSelectorOpen(false)}
        onSelect={(type) => {
          if (type === 'questionnaire') {
            setQuestionnaireCreateOpen(true);
          } else {
            setCreateOpen(true);
          }
        }}
      />
      {createOpen && (
        <TagForumTaggedNodeCreator
          open={createOpen} 
          onClose={() => setCreateOpen(false)} 
          ws={ws} 
          graphNodes={allNodes}
          graphEdges={allEdges}
          allTagConfigs={allTagConfigs}
          defaultTag={activeTag}
          dialogTitle="新增想法"
          isReply={false}
          showTags={true}
          buildGroupContext={buildTaggedForumContext}
          mode="tagged-forum"
        />
      )}
      {questionnaireCreateOpen && (
        <QuestionnaireNodeCreator
          open={questionnaireCreateOpen}
          onClose={() => setQuestionnaireCreateOpen(false)}
          ws={ws}
          groupId={groupId}
          activityId={activityId}
        />
      )}

      {/* 小組工具面板 */}
      {!isGuestMode && (
        <GroupToolPanel 
          activityId={activityId}
          groupId={groupId}
          ws={ws}
          onNodeAdded={(newNode) => {
            // 重新載入節點數據
            getNodes();
          }}
          mode="tagged-forum"
        />
      )}
    </div>
  );
}
